/**
 * Style resolution — turn `LandingPageData.styles` entries into inline CSS
 * properties for a given instance at a given viewport.
 *
 * The flat-Map data model stores styles keyed by
 * `(sourceId, breakpointId, property, state)`. To render a single instance
 * we need to:
 *
 *   1. Look up the style sources that apply to the instance (via
 *      `styleSourceSelections`).
 *   2. For each (source, breakpoint, property, state) entry, decide if it
 *      applies to the current viewport (via the breakpoint cascade) and
 *      state (`:hover`, `:focus`, `:active`, `:disabled`, base).
 *   3. Resolve token references to `var(--<token>)`; resolve literals
 *      verbatim.
 *   4. Compose into a flat React `CSSProperties`-friendly object,
 *      respecting source order (later sources win) and breakpoint
 *      progression (more specific breakpoint wins).
 *
 * Design choices the OSS research locked in:
 *
 * - **Browser cascade for media queries**: in v1 we do NOT compute the
 *   effective style per breakpoint and inline a single set. Instead we
 *   emit a `<style>` block with `@media (min-width: …)` rules and let the
 *   browser do the cascade. This avoids reimplementing the CSS engine
 *   (Webstudio's `@webstudio-is/css-engine` is ~4K LOC of work we can
 *   skip). The decision was confirmed in the OSS research brief.
 *
 * - **Token references → `var(--<name>)`**: the compiler emits raw CSS
 *   variable references; brand setup defines the variable values once
 *   per BU at the page root. Customers tweak brand → all pages update.
 *
 * - **Soft enforcement**: literals are tolerated. The renderer marks
 *   them with `data-oj-literal="true"` so brand audits can find them
 *   programmatically, but does not reject.
 *
 * The `resolveInstanceStyles` function returns the *inline base style*
 * (no media queries) plus a CSS string for the breakpoint and state
 * variants. Renderers apply both via `style={inlineStyle}` and a
 * scoped `<style>` element when there are non-base entries.
 */

import type {
  Breakpoint,
  LandingPageData,
  NodeId,
  PseudoState,
  SourceId,
  StyleDecl,
  StyleValue,
} from './types';

// ─────────────────────────────────────────────────────────────────────────
// Resolution result
// ─────────────────────────────────────────────────────────────────────────

export interface ResolvedStyle {
  /** Inline `style` props for the base breakpoint, no pseudo-state.
   *  React-compatible (camelCase property names). */
  inline: Record<string, string>;
  /** CSS rules for non-base breakpoints and pseudo-states. Empty string
   *  if the instance has no responsive or stateful overrides. The
   *  renderer wraps this in a scoped `<style>` element keyed by
   *  `data-oj-id="<nodeId>"`. */
  css: string;
  /** True if any literal style values were resolved. Drives the
   *  `data-oj-literal` attribute on the rendered element for audit. */
  hasLiterals: boolean;
}

const EMPTY_RESULT: ResolvedStyle = { inline: {}, css: '', hasLiterals: false };

// ─────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────

/**
 * Resolve all styles that apply to `instanceId` into inline CSS plus a
 * CSS string for breakpoint/state variants. Returns
 * `{ inline: {}, css: '', hasLiterals: false }` for instances with no
 * style declarations.
 *
 * Source-order: `styleSourceSelections.values` is ordered low-to-high
 * priority. Later entries win on conflict.
 *
 * Breakpoint-order: base breakpoint (`minWidth: 0`) is the foundation;
 * higher-`minWidth` breakpoints override via `@media (min-width: …)` in
 * the emitted CSS.
 */
export function resolveInstanceStyles(
  data: LandingPageData,
  instanceId: NodeId,
): ResolvedStyle {
  const selection = data.styleSourceSelections.get(instanceId);
  if (!selection || selection.values.length === 0) return EMPTY_RESULT;

  // Identify the base breakpoint (`minWidth: 0`). There must be exactly
  // one in `DEFAULT_BREAKPOINTS`; if a customer shipped a custom set
  // without one, we fall back to the lowest minWidth.
  const baseBreakpoint = pickBaseBreakpoint(data);
  if (!baseBreakpoint) return EMPTY_RESULT;

  const inline: Record<string, string> = {};
  const responsiveEntries: Array<{ bp: Breakpoint; decl: StyleDecl }> = [];
  const stateEntries: Array<{ decl: StyleDecl }> = [];
  let hasLiterals = false;

  // For each source in priority order, walk all entries that match.
  // Later entries overwrite earlier ones in `inline`.
  for (const sourceId of selection.values) {
    for (const decl of iterateDeclsForSource(data, sourceId)) {
      const isBaseBp = decl.breakpointId === baseBreakpoint.id;
      const isBaseState = decl.state === '';

      if (decl.value.type === 'literal') hasLiterals = true;

      if (isBaseBp && isBaseState) {
        // Goes inline.
        inline[toReactPropName(decl.property)] = resolveValue(decl.value);
      } else if (!isBaseBp && isBaseState) {
        // Responsive override; lives in @media block.
        const bp = data.breakpoints.get(decl.breakpointId);
        if (bp) responsiveEntries.push({ bp, decl });
      } else {
        // State variant (:hover, :focus, etc.) — base breakpoint or
        // responsive. Emit as scoped CSS rules. We collect everything
        // here including responsive+state combos.
        stateEntries.push({ decl });
      }
    }
  }

  const css = composeCss(instanceId, responsiveEntries, stateEntries, data.breakpoints);

  return { inline, css, hasLiterals };
}

// ─────────────────────────────────────────────────────────────────────────
// Token / literal value resolution
// ─────────────────────────────────────────────────────────────────────────

/** Convert a `StyleValue` into a CSS string. Tokens become
 *  `var(--<name>)` references; literals are sanitized to prevent CSS
 *  injection.
 *
 *  Why sanitize: literal values get inlined into both `style="..."`
 *  attributes AND scoped `<style>...</style>` blocks. Inside a style
 *  block a malicious value like `red} </style><script>alert(1)</script>`
 *  closes the rule, closes the style tag, and injects script. The
 *  `<style>` block content is parsed as CDATA-like — only `</style>`
 *  closes the tag, but the literal `</` sequence is enough to break
 *  out. We block the three CSS-injection vectors:
 *
 *    - `}` (would close the rule)
 *    - `*` followed by `/` (closes a CSS comment, lets attacker hide
 *      hostile rules behind a `/*` comment opener)
 *    - `<` (used in `</style>` to break out of the wrapping tag)
 *
 *  The replacements use CSS hex escapes (`\7d ` etc.) which are
 *  pre-rendered by the browser as the original character — no visual
 *  diff for legitimate values that don't contain these characters,
 *  defense-in-depth for ones that do. Customers who genuinely need a
 *  `}` in a content-property string would also need to URL-encode it,
 *  matching CSS spec behavior. */
export function resolveValue(value: StyleValue): string {
  if (value.type === 'token') {
    return `var(--${value.tokenId})`;
  }
  return escapeCssValue(value.value);
}

/** Escape characters that could break out of a CSS rule or `<style>`
 *  block. Exported for tests; callers should never need it directly
 *  because resolveValue applies it automatically. */
export function escapeCssValue(value: string): string {
  return value.replace(/}|\*\/|<\//g, (m) => {
    if (m === '}') return '\\7d ';
    if (m === '*/') return '*\\2f ';
    return '\\3c /'; // `</` → keeps the slash for CSS-syntax round-trip
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────

/** Pick the base breakpoint — the one with `minWidth: 0`. Falls back to
 *  the lowest-minWidth entry if no zero exists, or undefined if the
 *  page has no breakpoints (a malformed document). */
function pickBaseBreakpoint(data: LandingPageData): Breakpoint | undefined {
  let lowest: Breakpoint | undefined;
  for (const bp of data.breakpoints.values()) {
    if (bp.minWidth === 0) return bp;
    if (!lowest || bp.minWidth < lowest.minWidth) lowest = bp;
  }
  return lowest;
}

/** Yield every `StyleDecl` with the given `sourceId`. Linear scan over
 *  `data.styles`; the Map is small (typical pages have <100 styles)
 *  so the cost is negligible. */
function* iterateDeclsForSource(
  data: LandingPageData,
  sourceId: SourceId,
): Generator<StyleDecl> {
  for (const decl of data.styles.values()) {
    if (decl.sourceId === sourceId) yield decl;
  }
}

/** Convert a CSS property to React's camelCase form. Idempotent — if
 *  the input is already camelCase, returns it unchanged. */
function toReactPropName(property: string): string {
  if (!property.includes('-')) return property;
  return property.replace(/-([a-z])/g, (_, ch: string) => ch.toUpperCase());
}

/** Compose responsive + state declarations into a single CSS string for
 *  scoping under `[data-oj-id="<instanceId>"]`. */
function composeCss(
  instanceId: NodeId,
  responsive: Array<{ bp: Breakpoint; decl: StyleDecl }>,
  states: Array<{ decl: StyleDecl }>,
  breakpoints: Map<string, Breakpoint>,
): string {
  if (responsive.length === 0 && states.length === 0) return '';

  const selector = `[data-oj-id="${cssEscape(instanceId)}"]`;
  const parts: string[] = [];

  // Responsive entries grouped by breakpoint id so each @media block
  // contains all overrides for that breakpoint at once.
  const byBreakpoint = new Map<string, StyleDecl[]>();
  for (const { decl } of responsive) {
    const list = byBreakpoint.get(decl.breakpointId) ?? [];
    list.push(decl);
    byBreakpoint.set(decl.breakpointId, list);
  }
  // Iterate breakpoints in ascending minWidth order so the emitted CSS
  // matches the visual cascade order (mobile-first progression).
  const sortedBps = [...byBreakpoint.keys()].sort((a, b) => {
    const aMin = breakpoints.get(a)?.minWidth ?? 0;
    const bMin = breakpoints.get(b)?.minWidth ?? 0;
    return aMin - bMin;
  });
  for (const bpId of sortedBps) {
    const bp = breakpoints.get(bpId);
    if (!bp) continue;
    const decls = byBreakpoint.get(bpId)!;
    const cssDecls = decls.map((d) => `  ${cssPropertyName(d.property)}: ${resolveValue(d.value)};`).join('\n');
    parts.push(
      `@media (min-width: ${bp.minWidth}px) {\n  ${selector} {\n  ${cssDecls}\n  }\n}`,
    );
  }

  // State entries: group by (breakpointId, state) so each group emits
  // one rule. Base breakpoint state rules go outside @media; responsive
  // state rules go inside.
  const byBpAndState = new Map<string, { bpId: string; state: PseudoState; decls: StyleDecl[] }>();
  for (const { decl } of states) {
    const key = `${decl.breakpointId}|${decl.state}`;
    const entry = byBpAndState.get(key) ?? {
      bpId: decl.breakpointId,
      state: decl.state,
      decls: [] as StyleDecl[],
    };
    entry.decls.push(decl);
    byBpAndState.set(key, entry);
  }
  for (const { bpId, state, decls } of byBpAndState.values()) {
    const bp = breakpoints.get(bpId);
    if (!bp) continue;
    const cssDecls = decls.map((d) => `  ${cssPropertyName(d.property)}: ${resolveValue(d.value)};`).join('\n');
    const stateSelector = `${selector}${state}`;
    if (bp.minWidth === 0) {
      parts.push(`${stateSelector} {\n${cssDecls}\n}`);
    } else {
      parts.push(
        `@media (min-width: ${bp.minWidth}px) {\n  ${stateSelector} {\n  ${cssDecls}\n  }\n}`,
      );
    }
  }

  return parts.join('\n\n');
}

/** Convert a property name to its kebab-case CSS form. Idempotent. */
function cssPropertyName(property: string): string {
  if (property.includes('-')) return property;
  return property.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);
}

/** CSS-escape a string for use inside an attribute selector. We only
 *  need to handle the safe subset of characters node ids actually
 *  contain (alphanumeric, `-`, `_`); anything else gets backslash-
 *  escaped defensively. */
function cssEscape(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
